<template>
  <div class="hms-print-shell">
    <!-- Action bar — hidden on print -->
    <div class="hms-print-bar no-print">
      <div class="hms-print-bar-inner">
        <div>
          <span class="hms-bar-title"><i class="mdi" :class="typeIcon"></i> {{ typeName }}</span>
          <span class="hms-muted-sm" v-if="documentNumber"> · {{ documentNumber }}</span>
        </div>
        <div class="hms-bar-actions">
          <button class="hms-btn hms-btn-blue" @click="goBack"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
          <button class="hms-btn hms-btn-primary" @click="doPrint"><i class="mdi mdi-printer me-1"></i>Print</button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="hms-spinner no-print"><div class="spinner-border"></div></div>

    <div v-else-if="!data.Header" class="hms-empty no-print">
      <i class="mdi mdi-alert-circle-outline"></i>{{ error || "Document not found." }}
    </div>

    <div v-else class="hms-page-printable" :class="'doc-' + type">
      <!-- ========== HEADER (shared across all docs) ========== -->
      <header class="hms-doc-header">
        <div class="hms-doc-h-left">
          <img v-if="data.Header.logo_url" :src="data.Header.logo_url" alt="logo" class="hms-doc-logo" />
          <i v-else class="mdi mdi-hospital-building hms-doc-logo-fallback"></i>
        </div>
        <div class="hms-doc-h-mid">
          <div class="hms-doc-h-name">{{ data.Header.hospital_name }}</div>
          <div class="hms-doc-h-sub">{{ data.Header.branch_name }}<span v-if="data.Header.kmfl_code"> · KMFL {{ data.Header.kmfl_code }}</span></div>
          <div class="hms-doc-h-sub">{{ [data.Header.branch_address, data.Header.hospital_city, data.Header.country].filter(Boolean).join(', ') }}</div>
          <div class="hms-doc-h-sub">
            <span v-if="data.Header.branch_phone || data.Header.hospital_phone"><i class="mdi mdi-phone"></i> {{ data.Header.branch_phone || data.Header.hospital_phone }}</span>
            <span v-if="data.Header.hospital_email" class="ms-2"><i class="mdi mdi-email-outline"></i> {{ data.Header.hospital_email }}</span>
          </div>
        </div>
        <div class="hms-doc-h-right">
          <div class="hms-doc-type-band">{{ typeName }}</div>
          <div class="hms-doc-num" v-if="documentNumber">{{ documentNumber }}</div>
          <div class="hms-doc-date">{{ documentDate }}</div>
        </div>
      </header>

      <!-- ========== INVOICE ========== -->
      <section v-if="type === 'invoice' && data.Invoice" class="hms-doc-body">
        <div class="hms-doc-grid mb-3">
          <div>
            <div class="hms-doc-label">BILL TO</div>
            <div class="hms-doc-strong">{{ data.Invoice.patient_name }}</div>
            <div class="hms-doc-sub">{{ data.Invoice.patient_no }} · {{ ageStr(data.Invoice.dob) }} · {{ data.Invoice.gender }}</div>
            <div class="hms-doc-sub" v-if="data.Invoice.phone">{{ data.Invoice.phone }}</div>
            <div class="hms-doc-sub" v-if="data.Invoice.patient_address">{{ data.Invoice.patient_address }}</div>
            <div class="hms-doc-sub" v-if="data.Invoice.insurance_provider"><strong>{{ data.Invoice.insurance_provider }}</strong> · {{ data.Invoice.insurance_number || '—' }}</div>
          </div>
          <div>
            <div class="hms-doc-label">INVOICE</div>
            <div class="hms-doc-kv"><span>Number:</span><span class="mono">{{ data.Invoice.invoice_number }}</span></div>
            <div class="hms-doc-kv"><span>Date:</span><span>{{ formatDate(data.Invoice.invoice_date) }}</span></div>
            <div class="hms-doc-kv" v-if="data.Invoice.due_date"><span>Due:</span><span>{{ formatDate(data.Invoice.due_date) }}</span></div>
            <div class="hms-doc-kv"><span>Status:</span><span class="hms-doc-badge">{{ formatStatus(data.Invoice.payment_status) }}</span></div>
          </div>
        </div>

        <table class="hms-doc-table">
          <thead><tr><th>#</th><th>Description</th><th>Category</th><th>Qty</th><th class="ta-r">Unit (KSh)</th><th class="ta-r">Total (KSh)</th></tr></thead>
          <tbody>
            <tr v-for="(it, i) in data.Items" :key="i">
              <td>{{ i + 1 }}</td>
              <td>{{ it.item_description }}</td>
              <td>{{ it.item_category }}</td>
              <td>{{ it.quantity }}</td>
              <td class="ta-r">{{ formatNum(it.unit_price) }}</td>
              <td class="ta-r"><strong>{{ formatNum(it.total_price) }}</strong></td>
            </tr>
          </tbody>
        </table>

        <div class="hms-doc-totals">
          <div class="hms-doc-tot-row"><span>Subtotal</span><span>{{ formatNum(data.Invoice.subtotal || data.Invoice.total_amount) }}</span></div>
          <div class="hms-doc-tot-row" v-if="data.Invoice.discount_amount > 0"><span>Discount</span><span>-{{ formatNum(data.Invoice.discount_amount) }}</span></div>
          <div class="hms-doc-tot-row" v-if="data.Invoice.tax_amount > 0"><span>Tax</span><span>{{ formatNum(data.Invoice.tax_amount) }}</span></div>
          <div class="hms-doc-tot-row hms-doc-tot-grand"><span>TOTAL</span><span>KSh {{ formatNum(data.Invoice.total_amount) }}</span></div>
          <div class="hms-doc-tot-row"><span>Paid</span><span>KSh {{ formatNum(data.Invoice.paid_amount) }}</span></div>
          <div class="hms-doc-tot-row hms-doc-tot-balance"><span>BALANCE DUE</span><span>KSh {{ formatNum(data.Invoice.balance) }}</span></div>
        </div>

        <div class="hms-doc-payments mt-3" v-if="data.Payments && data.Payments.length">
          <div class="hms-doc-label">PAYMENT HISTORY</div>
          <table class="hms-doc-table compact">
            <thead><tr><th>Date</th><th>Receipt #</th><th>Method</th><th>Reference</th><th class="ta-r">Amount</th></tr></thead>
            <tbody>
              <tr v-for="p in data.Payments" :key="p.payment_id">
                <td>{{ formatDate(p.payment_date) }}</td>
                <td class="mono">{{ p.payment_number }}</td>
                <td>{{ formatPay(p.payment_method) }}</td>
                <td class="mono">{{ p.mpesa_reference || p.bank_reference || p.card_reference || '—' }}</td>
                <td class="ta-r">{{ formatNum(p.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="hms-doc-pay-instr mt-3">
          <strong>Payment Instructions:</strong> M-Pesa Paybill <strong>{{ paybill }}</strong>, Account: {{ data.Invoice.patient_no }}.
          Or pay at the cashier on this invoice number.
        </div>
      </section>

      <!-- ========== RECEIPT ========== -->
      <section v-if="type === 'receipt' && data.Payment" class="hms-doc-body">
        <div class="hms-doc-grid mb-3">
          <div>
            <div class="hms-doc-label">RECEIVED FROM</div>
            <div class="hms-doc-strong">{{ data.Payment.patient_name }}</div>
            <div class="hms-doc-sub">{{ data.Payment.patient_no }}</div>
            <div class="hms-doc-sub" v-if="data.Payment.phone">{{ data.Payment.phone }}</div>
          </div>
          <div>
            <div class="hms-doc-label">RECEIPT</div>
            <div class="hms-doc-kv"><span>Number:</span><span class="mono">{{ data.Payment.payment_number }}</span></div>
            <div class="hms-doc-kv"><span>Date:</span><span>{{ formatDateTime(data.Payment.payment_date) }}</span></div>
            <div class="hms-doc-kv"><span>For Invoice:</span><span class="mono">{{ data.Payment.invoice_number }}</span></div>
          </div>
        </div>

        <div class="hms-doc-receipt-amount">
          <div class="hms-doc-label">AMOUNT RECEIVED</div>
          <div class="hms-doc-receipt-num">KSh {{ formatNum(data.Payment.amount) }}</div>
          <div class="hms-doc-receipt-words">{{ amountInWords(data.Payment.amount) }} only</div>
        </div>

        <table class="hms-doc-table compact mt-3">
          <tbody>
            <tr><th class="ta-l">Payment Method</th><td>{{ formatPay(data.Payment.payment_method) }}</td></tr>
            <tr v-if="data.Payment.mpesa_reference"><th class="ta-l">M-Pesa Reference</th><td class="mono">{{ data.Payment.mpesa_reference }}</td></tr>
            <tr v-if="data.Payment.bank_reference"><th class="ta-l">Bank Reference</th><td class="mono">{{ data.Payment.bank_reference }}</td></tr>
            <tr v-if="data.Payment.card_reference"><th class="ta-l">Card Reference</th><td class="mono">{{ data.Payment.card_reference }}</td></tr>
            <tr v-if="data.Payment.notes"><th class="ta-l">Notes</th><td>{{ data.Payment.notes }}</td></tr>
            <tr><th class="ta-l">Invoice Total</th><td>KSh {{ formatNum(data.Payment.invoice_total) }}</td></tr>
            <tr><th class="ta-l">Outstanding Balance</th><td><strong>KSh {{ formatNum(data.Payment.invoice_balance) }}</strong></td></tr>
          </tbody>
        </table>

        <div class="hms-doc-sig-row mt-4">
          <div>
            <div class="hms-doc-sig-line"></div>
            <div class="hms-doc-sig-lbl">Received by · {{ data.Payment.received_by_name || '—' }}</div>
          </div>
          <div>
            <div class="hms-doc-sig-line"></div>
            <div class="hms-doc-sig-lbl">Cashier stamp / signature</div>
          </div>
        </div>
      </section>

      <!-- ========== LAB REPORT ========== -->
      <section v-if="type === 'lab' && data.Request" class="hms-doc-body">
        <div class="hms-doc-grid mb-3">
          <div>
            <div class="hms-doc-label">PATIENT</div>
            <div class="hms-doc-strong">{{ data.Request.patient_name }}</div>
            <div class="hms-doc-sub">{{ data.Request.patient_no }} · {{ ageStr(data.Request.dob) }} · {{ data.Request.gender }}</div>
          </div>
          <div>
            <div class="hms-doc-label">REQUEST</div>
            <div class="hms-doc-kv"><span>Number:</span><span class="mono">{{ data.Request.request_number }}</span></div>
            <div class="hms-doc-kv"><span>Date:</span><span>{{ formatDateTime(data.Request.request_date) }}</span></div>
            <div class="hms-doc-kv"><span>Priority:</span><span><span class="hms-doc-badge" :class="'pri-' + data.Request.priority">{{ data.Request.priority }}</span></span></div>
            <div class="hms-doc-kv"><span>Requested by:</span><span>{{ data.Request.requested_by_name }}</span></div>
          </div>
        </div>

        <div v-if="data.Request.clinical_notes" class="hms-doc-clinical mb-3">
          <strong>Clinical Notes:</strong> {{ data.Request.clinical_notes }}
        </div>

        <table class="hms-doc-table">
          <thead><tr><th>Test</th><th>Sample</th><th>Result</th><th>Unit</th><th>Range</th><th>Flag</th><th>Verified by</th></tr></thead>
          <tbody>
            <tr v-for="(it, i) in data.Items" :key="i">
              <td><strong>{{ it.test_name }}</strong></td>
              <td>{{ it.sample_type || '—' }}</td>
              <td><strong :class="flagClass(it.result_flag)">{{ it.result_value || '—' }}</strong></td>
              <td>{{ it.result_unit || '—' }}</td>
              <td>{{ it.normal_range || '—' }}</td>
              <td v-if="it.result_flag"><span class="hms-doc-badge" :class="flagBadgeClass(it.result_flag)">{{ it.result_flag.replace('_',' ') }}</span></td>
              <td v-else>—</td>
              <td>{{ it.verified_by_name || it.result_by_name || '—' }}</td>
            </tr>
          </tbody>
        </table>

        <div class="hms-doc-sig-row mt-4">
          <div>
            <div class="hms-doc-sig-line"></div>
            <div class="hms-doc-sig-lbl">Lab Technologist Signature & Stamp</div>
          </div>
          <div>
            <div class="hms-doc-sig-line"></div>
            <div class="hms-doc-sig-lbl">Pathologist Verification (where applicable)</div>
          </div>
        </div>
      </section>

      <!-- ========== PRESCRIPTION ========== -->
      <section v-if="type === 'rx' && data.Prescription" class="hms-doc-body">
        <div class="hms-doc-grid mb-3">
          <div>
            <div class="hms-doc-label">PATIENT</div>
            <div class="hms-doc-strong">{{ data.Prescription.patient_name }}</div>
            <div class="hms-doc-sub">{{ data.Prescription.patient_no }} · {{ ageStr(data.Prescription.dob) }} · {{ data.Prescription.gender }}</div>
            <div class="hms-doc-warn mt-1" v-if="data.Prescription.allergies">
              <i class="mdi mdi-alert-octagon"></i> ALLERGIES: {{ data.Prescription.allergies }}
            </div>
          </div>
          <div>
            <div class="hms-doc-label">RX</div>
            <div class="hms-doc-kv"><span>Number:</span><span class="mono">{{ data.Prescription.prescription_number }}</span></div>
            <div class="hms-doc-kv"><span>Date:</span><span>{{ formatDate(data.Prescription.prescription_date) }}</span></div>
            <div class="hms-doc-kv"><span>Status:</span><span class="hms-doc-badge">{{ data.Prescription.status }}</span></div>
          </div>
        </div>

        <div class="hms-doc-rx-symbol">℞</div>

        <table class="hms-doc-table">
          <thead><tr><th>#</th><th>Medication</th><th>Dosage</th><th>Frequency</th><th>Duration</th><th>Route</th><th>Qty</th></tr></thead>
          <tbody>
            <tr v-for="(it, i) in data.Items" :key="i">
              <td>{{ i + 1 }}</td>
              <td><strong>{{ it.drug_name }}</strong></td>
              <td>{{ it.dosage }}</td>
              <td>{{ it.frequency }}</td>
              <td>{{ it.duration }}</td>
              <td>{{ it.route }}</td>
              <td>{{ it.quantity_prescribed }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="data.Prescription.notes" class="hms-doc-clinical mt-3">
          <strong>Notes:</strong> {{ data.Prescription.notes }}
        </div>

        <div class="hms-doc-sig-row mt-4">
          <div>
            <div class="hms-doc-sig-line"></div>
            <div class="hms-doc-sig-lbl">
              <strong>{{ data.Prescription.prescriber_name || '—' }}</strong>
              <span v-if="data.Prescription.prescriber_license"> · Licence {{ data.Prescription.prescriber_license }}</span>
            </div>
          </div>
          <div>
            <div class="hms-doc-sig-line"></div>
            <div class="hms-doc-sig-lbl">Pharmacist (dispensed)</div>
          </div>
        </div>
      </section>

      <!-- ========== DISCHARGE SUMMARY ========== -->
      <section v-if="type === 'discharge' && data.Admission" class="hms-doc-body">
        <div class="hms-doc-grid mb-3">
          <div>
            <div class="hms-doc-label">PATIENT</div>
            <div class="hms-doc-strong">{{ data.Admission.patient_name }}</div>
            <div class="hms-doc-sub">{{ data.Admission.patient_no }} · {{ ageStr(data.Admission.dob) }} · {{ data.Admission.gender }}</div>
            <div class="hms-doc-sub" v-if="data.Admission.next_of_kin_name">NoK: {{ data.Admission.next_of_kin_name }} · {{ data.Admission.next_of_kin_phone }}</div>
            <div class="hms-doc-sub" v-if="data.Admission.insurance_provider">{{ data.Admission.insurance_provider }} · {{ data.Admission.insurance_number || '—' }}</div>
            <div class="hms-doc-warn mt-1" v-if="data.Admission.allergies">
              <i class="mdi mdi-alert-octagon"></i> ALLERGIES: {{ data.Admission.allergies }}
            </div>
          </div>
          <div>
            <div class="hms-doc-label">ADMISSION</div>
            <div class="hms-doc-kv"><span>Number:</span><span class="mono">{{ data.Admission.admission_number }}</span></div>
            <div class="hms-doc-kv"><span>Type:</span><span>{{ data.Admission.admission_type }}</span></div>
            <div class="hms-doc-kv"><span>Admitted:</span><span>{{ formatDateTime(data.Admission.admission_date) }}</span></div>
            <div class="hms-doc-kv" v-if="data.Admission.actual_discharge_date"><span>Discharged:</span><span>{{ formatDateTime(data.Admission.actual_discharge_date) }}</span></div>
            <div class="hms-doc-kv"><span>LOS:</span><span>{{ losDays }} days</span></div>
            <div class="hms-doc-kv"><span>Ward / Bed:</span><span>{{ data.Admission.ward_name }} · {{ data.Admission.bed_number }}</span></div>
            <div class="hms-doc-kv"><span>Condition:</span><span>{{ data.Admission.discharge_condition || '—' }}</span></div>
          </div>
        </div>

        <div class="hms-doc-section">
          <div class="hms-doc-section-h">DIAGNOSIS ON ADMISSION</div>
          <div>{{ data.Admission.diagnosis_on_admission || '—' }}</div>
        </div>

        <div class="hms-doc-section" v-if="data.Admission.discharge_diagnosis">
          <div class="hms-doc-section-h">DISCHARGE DIAGNOSIS</div>
          <div><strong>{{ data.Admission.discharge_diagnosis }}</strong></div>
        </div>

        <div class="hms-doc-section" v-if="data.Diagnoses && data.Diagnoses.length">
          <div class="hms-doc-section-h">OTHER DIAGNOSES</div>
          <ul class="hms-doc-list">
            <li v-for="(d, i) in data.Diagnoses" :key="i">
              <strong>{{ d.diagnosis_name }}</strong>
              <span v-if="d.icd_code"> ({{ d.icd_code }})</span>
              <span v-if="d.diagnosis_type"> · {{ d.diagnosis_type }}</span>
            </li>
          </ul>
        </div>

        <div class="hms-doc-section" v-if="data.Admission.discharge_summary">
          <div class="hms-doc-section-h">CLINICAL SUMMARY</div>
          <div class="hms-doc-pre">{{ data.Admission.discharge_summary }}</div>
        </div>

        <div class="hms-doc-section" v-if="data.Notes && data.Notes.length">
          <div class="hms-doc-section-h">PROGRESS NOTES (latest)</div>
          <div v-for="(n, i) in data.Notes.slice(0, 2)" :key="i" class="hms-doc-prior-note">
            <div class="hms-doc-sub"><strong>{{ n.doctor_name }}</strong> · {{ formatDateTime(n.created_at) }}</div>
            <div v-if="n.subjective"><strong>S:</strong> {{ n.subjective }}</div>
            <div v-if="n.objective"><strong>O:</strong> {{ n.objective }}</div>
            <div v-if="n.assessment"><strong>A:</strong> {{ n.assessment }}</div>
            <div v-if="n.plan"><strong>P:</strong> {{ n.plan }}</div>
          </div>
        </div>

        <div class="hms-doc-section" v-if="data.Medications && data.Medications.length">
          <div class="hms-doc-section-h">DISCHARGE MEDICATIONS</div>
          <div v-for="(p, i) in data.Medications" :key="i" class="hms-doc-prior-note">
            <div class="hms-doc-sub"><strong>{{ p.prescription_number }}</strong></div>
            <pre class="hms-doc-pre">{{ p.meds }}</pre>
            <div v-if="p.notes" class="hms-doc-sub">{{ p.notes }}</div>
          </div>
        </div>

        <div class="hms-doc-section">
          <div class="hms-doc-section-h">FOLLOW-UP INSTRUCTIONS</div>
          <ul class="hms-doc-list">
            <li>Take all prescribed medications as directed.</li>
            <li>Attend follow-up review at {{ data.Header.hospital_name }} as advised.</li>
            <li>Return immediately if symptoms worsen — call {{ data.Header.branch_phone || data.Header.hospital_phone }}.</li>
            <li>Bring this discharge summary to all subsequent visits.</li>
          </ul>
        </div>

        <div class="hms-doc-sig-row mt-4">
          <div>
            <div class="hms-doc-sig-line"></div>
            <div class="hms-doc-sig-lbl">
              <strong>{{ data.Admission.doctor_name || '—' }}</strong>
              <span v-if="data.Admission.doctor_license"> · Licence {{ data.Admission.doctor_license }}</span>
            </div>
          </div>
          <div>
            <div class="hms-doc-sig-line"></div>
            <div class="hms-doc-sig-lbl">Patient / Caregiver acknowledgement</div>
          </div>
        </div>
      </section>

      <!-- ========== VISIT / QUEUE HISTORY ========== -->
      <section v-if="type === 'visit_history' && data.Patient" class="hms-doc-body">
        <div class="hms-doc-grid mb-3">
          <div>
            <div class="hms-doc-label">PATIENT</div>
            <div class="hms-doc-strong">{{ data.Patient.patient_name }}</div>
            <div class="hms-doc-sub">{{ data.Patient.patient_no }} · {{ ageStr(data.Patient.dob) }} · {{ data.Patient.gender }}</div>
            <div class="hms-doc-sub" v-if="data.Patient.phone">{{ data.Patient.phone }}</div>
          </div>
          <div>
            <div class="hms-doc-label">SUMMARY</div>
            <div class="hms-doc-kv"><span>Visits:</span><span>{{ (data.Visits || []).length }}</span></div>
            <div class="hms-doc-kv"><span>Queue tickets:</span><span>{{ (data.Tickets || []).length }}</span></div>
          </div>
        </div>

        <div class="hms-doc-section">
          <div class="hms-doc-section-h">VISITS</div>
          <table class="hms-doc-table" v-if="data.Visits && data.Visits.length">
            <thead><tr><th>Visit #</th><th>Date</th><th>Type</th><th>Complaint</th><th>Doctor</th><th>Status / Disposition</th></tr></thead>
            <tbody>
              <tr v-for="(v, i) in data.Visits" :key="i">
                <td class="mono">{{ v.visit_number }}</td>
                <td>{{ formatDateTime(v.arrival_time || v.visit_date) }}</td>
                <td>{{ v.visit_type }}</td>
                <td>{{ v.chief_complaint || '—' }}</td>
                <td>{{ v.doctor_name || '—' }}</td>
                <td>{{ v.disposition || v.visit_status }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else>No visits recorded.</div>
        </div>

        <div class="hms-doc-section">
          <div class="hms-doc-section-h">DEPARTMENT QUEUE TRAIL</div>
          <table class="hms-doc-table" v-if="data.Tickets && data.Tickets.length">
            <thead><tr><th>Ticket</th><th>Date</th><th>Department</th><th>Status</th><th>Waited</th><th>Served in</th><th>Served by</th></tr></thead>
            <tbody>
              <tr v-for="(t, i) in data.Tickets" :key="i">
                <td class="mono">{{ t.ticket_number }}</td>
                <td>{{ formatDateTime(t.issued_at) }}</td>
                <td>{{ t.dept_name || '—' }}</td>
                <td>{{ t.status }}</td>
                <td>{{ t.wait_minutes != null ? t.wait_minutes + ' min' : '—' }}</td>
                <td>{{ t.serve_minutes != null ? t.serve_minutes + ' min' : '—' }}</td>
                <td>{{ t.served_by_name || '—' }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else>No queue tickets recorded.</div>
        </div>
      </section>

      <!-- ========== FOOTER ========== -->
      <footer class="hms-doc-footer">
        Generated {{ generatedAt }} by HMS · This is an electronic record.
      </footer>
    </div>
  </div>
</template>

<style>
/* Print-only CSS — applies to the whole page */
@media print {
  body { background: #fff !important; margin: 0 !important; }
  .no-print { display: none !important; }
  .hms-page-printable { box-shadow: none !important; padding: 12mm !important; max-width: none !important; }
}
</style>

<style scoped>
.hms-print-shell { min-height: 100vh; background: #f4f6f9; padding: 0; }

.hms-print-bar { position: sticky; top: 0; background: #fff; border-bottom: 1px solid #dee2e6; box-shadow: 0 2px 8px rgba(0,0,0,.05); z-index: 100; }
.hms-print-bar-inner { max-width: 920px; margin: 0 auto; padding: 12px 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.hms-bar-title { color: #1a1a1a; font-weight: 700; }
.hms-bar-title i { color: #1a7fe8; margin-right: 4px; }
.hms-muted-sm { color: #6c757d; font-size: 12px; }
.hms-bar-actions { display: flex; gap: 8px; }
.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-btn-primary { background: #1a7fe8; color: #fff; }
.hms-btn-primary:hover { filter: brightness(1.1); }
.hms-btn-blue { background: rgba(26,127,232,0.12); color: #1a7fe8; border: 1px solid rgba(26,127,232,0.12); }

.hms-spinner { display: flex; justify-content: center; padding: 80px; }
.hms-spinner .spinner-border { color: #1a7fe8; }
.hms-empty { text-align: center; padding: 80px 20px; color: #6c757d; }
.hms-empty i { font-size: 48px; color: #ced4da; display: block; margin-bottom: 12px; }

/* Printable document */
.hms-page-printable {
  background: #fff;
  color: #000;
  font-family: 'Times New Roman', Georgia, serif;
  font-size: 12pt;
  line-height: 1.4;
  max-width: 800px;
  margin: 24px auto;
  padding: 32px 40px;
  box-shadow: 0 8px 24px rgba(0,0,0,.08);
}

.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11pt; }
.ta-l { text-align: left; }
.ta-r { text-align: right; }
.mt-3 { margin-top: 16px; }
.mt-4 { margin-top: 24px; }
.mb-3 { margin-bottom: 16px; }
.ms-2 { margin-left: 8px; }

/* Header */
.hms-doc-header { display: grid; grid-template-columns: 80px 1fr auto; gap: 16px; align-items: flex-start; padding-bottom: 14px; margin-bottom: 14px; border-bottom: 3px double #1a1a1a; }
.hms-doc-logo { width: 70px; height: 70px; object-fit: contain; }
.hms-doc-logo-fallback { font-size: 60px; color: #1a7fe8; }
.hms-doc-h-name { font-size: 18pt; font-weight: 700; color: #1a1a1a; line-height: 1.1; }
.hms-doc-h-sub { font-size: 10pt; color: #555; margin-top: 2px; }
.hms-doc-h-right { text-align: right; }
.hms-doc-type-band { background: #1a1a1a; color: #fff; padding: 4px 14px; font-size: 11pt; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; display: inline-block; }
.hms-doc-num { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12pt; margin-top: 6px; font-weight: 700; }
.hms-doc-date { font-size: 10pt; color: #555; margin-top: 4px; }

/* Two-col header info */
.hms-doc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
.hms-doc-label { font-size: 9pt; font-weight: 700; color: #555; letter-spacing: 1.2px; margin-bottom: 4px; }
.hms-doc-strong { font-size: 13pt; font-weight: 700; color: #1a1a1a; }
.hms-doc-sub { font-size: 10pt; color: #555; }
.hms-doc-kv { display: flex; justify-content: space-between; gap: 12px; padding: 2px 0; font-size: 11pt; border-bottom: 1px dotted #ccc; }
.hms-doc-kv span:first-child { color: #555; }

.hms-doc-badge { background: #eaeaea; color: #1a1a1a; padding: 1px 8px; border-radius: 4px; font-size: 10pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-doc-badge.pri-stat   { background: #ffebee; color: #c62828; }
.hms-doc-badge.pri-urgent { background: #fff3e0; color: #e65100; }
.hms-doc-badge.flag-h, .hms-doc-badge.flag-critical_high { background: #ffebee; color: #c62828; }
.hms-doc-badge.flag-l, .hms-doc-badge.flag-critical_low  { background: #fff3e0; color: #e65100; }
.hms-doc-badge.flag-abnormal { background: #fff8e1; color: #b88600; }
.hms-doc-badge.flag-normal   { background: #e8f5e9; color: #2e7d32; }

/* Tables */
.hms-doc-table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 10.5pt; }
.hms-doc-table th { background: #f4f4f4; text-align: left; font-weight: 700; padding: 6px 10px; border: 1px solid #ccc; }
.hms-doc-table td { padding: 6px 10px; border: 1px solid #ccc; }
.hms-doc-table.compact th, .hms-doc-table.compact td { padding: 4px 8px; font-size: 10pt; }

/* Totals */
.hms-doc-totals { margin-top: 12px; margin-left: auto; width: 320px; }
.hms-doc-tot-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 11pt; }
.hms-doc-tot-grand { border-top: 2px solid #1a1a1a; border-bottom: 2px solid #1a1a1a; font-weight: 700; font-size: 13pt; padding: 8px 0; margin-top: 6px; }
.hms-doc-tot-balance { color: #c62828; font-weight: 700; font-size: 13pt; padding: 6px 0; }

/* Receipt */
.hms-doc-receipt-amount { background: #f9f9f9; border: 2px solid #1a1a1a; padding: 16px; text-align: center; margin: 16px 0; }
.hms-doc-receipt-num { font-size: 28pt; font-weight: 800; color: #1a1a1a; margin-top: 4px; }
.hms-doc-receipt-words { font-size: 11pt; color: #555; margin-top: 4px; font-style: italic; }

/* Pay instructions */
.hms-doc-pay-instr { font-size: 10pt; color: #555; padding: 10px; background: #f9f9f9; border-left: 4px solid #1a7fe8; }

/* Lab/Rx clinical box */
.hms-doc-clinical { font-size: 10pt; padding: 8px 12px; background: #f9f9f9; border-left: 4px solid #1a7fe8; }
.hms-doc-warn { font-size: 10pt; padding: 6px 10px; background: #ffebee; color: #c62828; border-left: 4px solid #c62828; font-weight: 700; }

.flag-h, .flag-critical_high { color: #c62828; }
.flag-l, .flag-critical_low  { color: #e65100; }
.flag-abnormal { color: #b88600; }

/* Rx */
.hms-doc-rx-symbol { font-family: serif; font-size: 28pt; font-weight: 700; color: #1a7fe8; }

/* Discharge sections */
.hms-doc-section { margin-top: 14px; padding-top: 8px; border-top: 1px solid #e0e0e0; }
.hms-doc-section-h { font-size: 10pt; font-weight: 700; color: #555; letter-spacing: 1.2px; text-transform: uppercase; margin-bottom: 6px; }
.hms-doc-pre { white-space: pre-wrap; font-family: inherit; font-size: 11pt; }
.hms-doc-list { margin: 4px 0 0 18px; padding: 0; }
.hms-doc-list li { padding: 2px 0; font-size: 11pt; }
.hms-doc-prior-note { padding: 8px 12px; background: #f9f9f9; border: 1px solid #e0e0e0; border-radius: 6px; margin-bottom: 8px; font-size: 10.5pt; }

/* Signature lines */
.hms-doc-sig-row { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 32px; }
.hms-doc-sig-line { border-bottom: 1px solid #1a1a1a; height: 32px; }
.hms-doc-sig-lbl { font-size: 9pt; color: #555; margin-top: 4px; }

/* Footer */
.hms-doc-footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e0e0e0; font-size: 9pt; color: #888; text-align: center; }
</style>

<script>
/* eslint-disable */
import { executeJson, hms } from "@/api";

const TYPE_META = {
  invoice:   { name: 'Invoice',           icon: 'mdi-receipt-text-outline' },
  receipt:   { name: 'Payment Receipt',   icon: 'mdi-cash-check' },
  lab:       { name: 'Laboratory Report', icon: 'mdi-flask-outline' },
  rx:        { name: 'Prescription',      icon: 'mdi-pill' },
  discharge: { name: 'Discharge Summary', icon: 'mdi-home-export-outline' },
  visit_history: { name: 'Visit & Queue History', icon: 'mdi-history' }
};

export default {
  name: "PrintDocument",
  data() {
    return {
      loading: true, error: '',
      data: {},
      paybill: '522522',
      generatedAt: new Date().toLocaleString()
    };
  },
  computed: {
    type()      { return this.$route.params.type || ''; },
    id()        { return this.$route.params.id   || ''; },
    typeName()  { return (TYPE_META[this.type] || {}).name || 'Document'; },
    typeIcon()  { return (TYPE_META[this.type] || {}).icon || 'mdi-file-document-outline'; },
    documentNumber() {
      if (!this.data || !this.data.Header) return '';
      if (this.type === 'invoice')   return this.data.Invoice && this.data.Invoice.invoice_number;
      if (this.type === 'receipt')   return this.data.Payment && this.data.Payment.payment_number;
      if (this.type === 'lab')       return this.data.Request && this.data.Request.request_number;
      if (this.type === 'rx')        return this.data.Prescription && this.data.Prescription.prescription_number;
      if (this.type === 'discharge') return this.data.Admission && this.data.Admission.admission_number;
      if (this.type === 'visit_history') return this.data.Patient && this.data.Patient.patient_no;
      return '';
    },
    documentDate() {
      if (this.type === 'invoice')   return this.formatDate(this.data.Invoice && this.data.Invoice.invoice_date);
      if (this.type === 'receipt')   return this.formatDateTime(this.data.Payment && this.data.Payment.payment_date);
      if (this.type === 'lab')       return this.formatDateTime(this.data.Request && this.data.Request.request_date);
      if (this.type === 'rx')        return this.formatDate(this.data.Prescription && this.data.Prescription.prescription_date);
      if (this.type === 'discharge') return this.formatDate(this.data.Admission && (this.data.Admission.actual_discharge_date || this.data.Admission.admission_date));
      return '';
    },
    losDays() {
      if (!this.data.Admission) return 0;
      var s = this.data.Admission.admission_date;
      var e = this.data.Admission.actual_discharge_date || new Date().toISOString();
      if (!s) return 0;
      var d1 = new Date(s.replace ? s.replace(' ','T') : s);
      var d2 = new Date(e.replace ? e.replace(' ','T') : e);
      return Math.max(0, Math.floor((d2 - d1) / (24 * 3600 * 1000)));
    }
  },
  mounted() {
    // Print pages open in a new tab where sessionStorage is empty by design.
    // Only the bearer token (in localStorage, shared across tabs) is required
    // for getPrintData to succeed.
    if (!localStorage.getItem("authToken")) { this.$router.push("/login"); return; }
    if (!sessionStorage.getItem("isLoggedIn")) { sessionStorage.setItem("isLoggedIn", "1"); }
    document.documentElement.setAttribute("data-theme", "light"); // Force light for print
    this.load();
  },
  methods: {
    async load() {
      this.loading = true; this.error = '';
      try {
        var r = await executeJson({ function: "getPrintData", type: this.type, id: this.id }, hms);
        if (r.data && r.data.success) {
          this.data = r.data.data || {};
          // Pull paybill from settings if available
          this.$nextTick(() => { this.tryAutoPrint(); });
        } else {
          this.error = (r.data && r.data.message) || 'Failed to load document.';
        }
      } catch(e) { this.error = 'Connection error.'; }
      this.loading = false;
    },
    tryAutoPrint() {
      // Auto-print only when ?auto=1 in query
      if (this.$route.query.auto === '1') {
        setTimeout(() => window.print(), 500);
      }
    },
    doPrint() { window.print(); },
    goBack() { window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/dashboard'); },

    formatDate(s)     { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleDateString(); },
    formatDateTime(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); },
    formatNum(n)      { if (n == null || n === '') return '0'; return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    formatPay(p)      { return ({ cash:'Cash', mpesa:'M-Pesa', card:'Card', bank_transfer:'Bank Transfer', insurance:'Insurance', corporate:'Corporate', waiver:'Waiver' })[p] || p; },
    formatStatus(s)   { return ({ paid:'Paid', partial:'Partial', unpaid:'Unpaid', insurance_pending:'Insurance Pending', waived:'Waived' })[s] || s; },
    ageStr(dob)       { if (!dob||dob==='0000-00-00'||dob==='1900-01-01') return '?'; var d=new Date(dob); if (isNaN(d)) return '—'; return Math.floor((Date.now()-d.getTime())/(365.25*24*3600000))+'y'; },
    flagClass(f)      { return f ? 'flag-' + f : ''; },
    flagBadgeClass(f) { return f ? 'flag-' + f : ''; },

    amountInWords(n) {
      // Simple Kenyan-style amount-in-words for receipts (up to millions)
      if (!n || isNaN(n)) return '';
      var num = Math.floor(parseFloat(n));
      var cents = Math.round((parseFloat(n) - num) * 100);
      var ones  = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
      var tens  = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
      function chunk(n) {
        if (n === 0) return '';
        if (n < 20) return ones[n];
        if (n < 100) return tens[Math.floor(n/10)] + (n%10 ? ' ' + ones[n%10] : '');
        return ones[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' ' + chunk(n%100) : '');
      }
      var parts = [];
      var million  = Math.floor(num / 1000000); num %= 1000000;
      var thousand = Math.floor(num / 1000);    num %= 1000;
      var hundred  = num;
      if (million)  parts.push(chunk(million)  + ' Million');
      if (thousand) parts.push(chunk(thousand) + ' Thousand');
      if (hundred)  parts.push(chunk(hundred));
      var result = parts.join(' ') || 'Zero';
      return 'Kenya Shillings ' + result + (cents > 0 ? ' and ' + chunk(cents) + ' Cents' : '');
    }
  }
};
</script>
